import { Button, Group, Text } from "@mantine/core"
import { CheckIcon } from "@phosphor-icons/react"
import { ReactNode } from "react"
import { useTranslation } from "react-i18next"
import { ReasonTooltip } from "../ui"
import classes from "./detail-action-row.module.css"
import { OrderStatusTag } from "./status-tag"

type Props = {
  status: string | null | undefined
  /** The decision exists for this order (REVIEWED, addressed to this business). */
  decisionOpen: boolean
  /** Why the decision is unavailable (permission), null when allowed. */
  approveReason: string | null
  declineReason: string | null
  onApprove: () => void
  onReject: () => void
  /** Further order actions shown right-aligned in the same card (e.g. cancel a confirmed order). */
  extra?: ReactNode
}

/**
 * Status card of Ord_PurchaseDetail: «Төлөв: [badge] Таны шийдвэрийг хүлээж байна» with the actions
 * right-aligned in it — Татгалзах (default) and Зөвшөөрөх (primary), never hidden, aria-disabled
 * with the permission code — plus any `extra` action.
 */
export function DetailActionRow({ status, decisionOpen, approveReason, declineReason, onApprove, onReject, extra }: Props) {
  const { t } = useTranslation("supplierSales")

  return (
    <Group className={classes.root} justify="space-between" align="center" wrap="wrap" gap="sm">
      <Group gap="sm">
        <Text fz={14} c="gray.8">{t("purchase.detail.actions.status")}:</Text>
        <OrderStatusTag code={status} perspective="received" />
        {decisionOpen && <Text fz={14} c="dark.6">{t("purchase.detail.actions.awaitingDecision")}</Text>}
      </Group>

      {(decisionOpen || extra) && (
        <Group gap="sm">
          {extra}
          {decisionOpen && (
            <>
              <ReasonTooltip reason={declineReason}>
                <Button variant="default" onClick={onReject}>
                  {t("purchase.reject")}
                </Button>
              </ReasonTooltip>
              <ReasonTooltip reason={approveReason}>
                <Button leftSection={<CheckIcon size={16} />} onClick={onApprove}>
                  {t("purchase.approve")}
                </Button>
              </ReasonTooltip>
            </>
          )}
        </Group>
      )}
    </Group>
  )
}
